import { useEffect, useState } from 'react'
import { getLoanTrustAccountAssignment, getTrustAccountOptions, setLoanTrustAccountAssignment } from 'services'

import type { IResolvedTrustAccount, ITrustAccountAssignment, ITrustAccountOption } from './type'

const PURPOSE_TITLES: Record<string, string> = {
  origination: 'Origination',
  construction: 'Construction',
  servicing: 'Servicing',
}

/** Why the loan is on this account, said plainly rather than as a code. */
const SOURCE_LABELS: Record<string, string> = {
  loan: 'Set on this loan',
  lender: "From the lender's own account",
  company: 'Company default',
}

const SOURCE_COLORS: Record<string, string> = {
  loan: 'bg-shade-blue/10 text-shade-blue',
  lender: 'bg-emerald-100 text-emerald-800',
  company: 'bg-slate-100 text-slate-700',
}

/**
 * The three trust accounts this loan's money moves through.
 *
 * Each purpose resolves in order: an account pinned on the loan, then the lender's own account
 * for that purpose, then the company default. Choosing one here pins it; choosing "Resolve
 * automatically" clears the pin and lets it fall back again. Only accounts set up for a purpose
 * are offered for it.
 */
export default function TrustAccountsPanel() {
  const [loading, setLoading] = useState(false)
  const [assignment, setAssignment] = useState<ITrustAccountAssignment | null>(null)
  const [options, setOptions] = useState<ITrustAccountOption[]>([])
  const [error, setError] = useState('')
  const [open, setOpen] = useState(false)

  const load = () => {
    setLoading(true)
    Promise.all([getLoanTrustAccountAssignment(), getTrustAccountOptions()])
      .then(([assignmentRes, optionsRes]) => {
        setAssignment(assignmentRes)
        setOptions(optionsRes?.options ?? [])
      })
      .catch(() => setAssignment(null))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const onChange = (purpose: string, value: string) => {
    setError('')
    setLoading(true)
    setLoanTrustAccountAssignment(purpose, value ? Number(value) : null)
      .then((res) => setAssignment(res))
      .catch((err) => setError(err?.message || 'Could not change the trust account'))
      .finally(() => setLoading(false))
  }

  if (!assignment) return null

  const resolved: IResolvedTrustAccount[] = assignment.resolved || []
  const summary = resolved
    .map((item) => `${PURPOSE_TITLES[item.purpose] || item.purpose}: ${item.name || 'not set'}`)
    .join('  ·  ')

  return (
    <div className="mb-4 border rounded shadow-sm bg-white">
      <button
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-gray-50"
        onClick={() => setOpen(!open)}
      >
        <span className="text-[14px]">
          <span className="font-variation-settings-600">Trust Accounts</span>
          <span className="text-gray-500 ml-3">{summary}</span>
        </span>
        <span className="text-[13px] text-shade-blue">{open ? 'Hide' : 'Change'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t">
          {assignment.lender && (
            <div className="text-[13px] text-gray-500 mb-3">
              Lender on this loan: <span className="text-gray-700">{assignment.lender.name}</span>
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-4">
            {resolved.map((item) => {
              const eligible = options.filter((option) => (option.purposes || []).includes(item.purpose))
              return (
                <div key={item.purpose}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[14px] font-variation-settings-600">
                      {PURPOSE_TITLES[item.purpose] || item.purpose}
                    </span>
                    {item.source && (
                      <span className={`rounded px-2 py-0.5 text-[11px] ${SOURCE_COLORS[item.source] || ''}`}>
                        {SOURCE_LABELS[item.source]}
                      </span>
                    )}
                  </div>
                  <select
                    className="text-[14px] border rounded px-2 py-2 w-full"
                    value={assignment.pinned?.[item.purpose] ? String(assignment.pinned[item.purpose]) : ''}
                    disabled={loading}
                    onChange={(e) => onChange(item.purpose, e.target.value)}
                  >
                    <option value="">
                      Resolve automatically{item.name ? ` (${item.name})` : ' - nothing configured'}
                    </option>
                    {eligible.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
              )
            })}
          </div>
          {!!error && <div className="text-[13px] text-red-600 mt-3">{error}</div>}
          <div className="text-[13px] text-gray-500 mt-3">
            Borrower payments on this loan land in the servicing account. Construction draws leave the construction
            account, and deposits taken before the loan funds sit in the origination account.
          </div>
        </div>
      )}
    </div>
  )
}
